export const ANALYTICS_CHOICE_KEY = 'postpop.analytics-choice.v1';
export const ANALYTICS_DISCLOSURE_VERSION = '2026-10-05';
const LAYER = 'postpopAnalyticsLayer';

// Keep Analytics separate from the advertising tag/CMP's dataLayer and gtag.
export function createAnalyticsConsent({ win, doc, measurementId }) {
  const enabled = /^G-[A-Z0-9]+$/.test(measurementId || '');
  const disableKey = `ga-disable-${measurementId}`;
  let choice = 'unknown';
  let storageFailure = false;
  let initialized = false;
  let ready = false;
  let configured = false;
  let script = null;
  let currentPage = null;
  let loginStatus = 'guest';
  const listeners = new Set();
  const notify = () => listeners.forEach((listener) => listener());
  // gtag uses Arguments objects; arrays use a different dataLayer protocol.
  function command() {
    win[LAYER].push(arguments);
  }
  const read = () => {
    try {
      const value = JSON.parse(win.localStorage.getItem(ANALYTICS_CHOICE_KEY));
      if (
        value?.version === ANALYTICS_DISCLOSURE_VERSION &&
        ['granted', 'denied'].includes(value.choice) &&
        Number.isFinite(value.at) &&
        value.at > 0 &&
        value.at <= Date.now()
      )
        return value.choice;
    } catch (_) {
      /* Unavailable or corrupt storage never grants permission. */
    }
    return 'unknown';
  };
  const clearCookies = () => {
    const names = ['_ga', `_ga_${measurementId.slice(2)}`];
    const host = win.location.hostname.split('.');
    const domains = [
      '',
      ...host.map((_, i) => host.slice(i).join('.')).filter((s) => s.includes('.')),
    ];
    names.forEach((name) =>
      domains.forEach((domain) => {
        doc.cookie = `${name}=; Max-Age=0; Path=/${domain ? `; Domain=${domain}` : ''}; SameSite=Lax`;
      }),
    );
  };
  const pageView = () => {
    // Existing page hooks wait for account/content data; do not fabricate a second view.
    if (currentPage?.url !== win.location.href) return;
    const params = currentPage.params;
    command('event', 'page_view', {
      ...params,
      page_location: win.location.href,
      page_title: doc.title,
      login_status: loginStatus,
      send_to: measurementId,
    });
  };
  const activate = () => {
    if (!enabled || choice !== 'granted') return;
    if (!ready) {
      // Even a delayed loader must not transmit after permission is withdrawn.
      win[disableKey] = true;
      if (script) return;
      win[LAYER] = win[LAYER] || [];
      script = doc.createElement('script');
      script.id = 'postpop-analytics-tag';
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}&l=${LAYER}`;
      script.onload = () => {
        ready = true;
        sync();
        if (choice === 'granted') activate();
      };
      script.onerror = () => {
        script.remove();
        script = null;
      };
      doc.head.appendChild(script);
      return;
    }
    win[disableKey] = false;
    command('consent', configured ? 'update' : 'default', { analytics_storage: 'granted' });
    if (!configured) {
      command('js', new Date());
      command('config', measurementId, {
        send_page_view: false,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
      });
      configured = true;
    }
    command('set', 'user_properties', { login_status: loginStatus });
    pageView();
  };
  const apply = (next) => {
    const changed = next !== choice;
    choice = next;
    if (enabled && next !== 'granted') {
      win[disableKey] = true;
      if (configured) command('consent', 'update', { analytics_storage: 'denied' });
      clearCookies();
    } else if (changed || (!ready && !script)) activate();
    if (changed) notify();
  };
  const sync = () => apply(storageFailure ? 'denied' : read());
  const storageChanged = (event) => {
    if (event.key === null || event.key === ANALYTICS_CHOICE_KEY) sync();
  };
  const visibilityChanged = () => {
    if (doc.visibilityState === 'visible') sync();
  };
  return {
    enabled,
    initialize() {
      if (initialized || !enabled) return;
      initialized = true;
      win[disableKey] = true;
      sync();
      win.addEventListener('storage', storageChanged);
      win.addEventListener('focus', sync);
      doc.addEventListener('visibilitychange', visibilityChanged);
    },
    getSnapshot: () => choice,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    setChoice(next) {
      if (!enabled || !['granted', 'denied'].includes(next)) return false;
      try {
        const value = JSON.stringify({
          version: ANALYTICS_DISCLOSURE_VERSION,
          choice: next,
          at: Date.now(),
        });
        win.localStorage.setItem(ANALYTICS_CHOICE_KEY, value);
        if (win.localStorage.getItem(ANALYTICS_CHOICE_KEY) !== value)
          throw new Error('Choice not stored');
      } catch (_) {
        // A failed opt-in must not start collection; a failed opt-out still stops it.
        storageFailure = true;
        try {
          win.localStorage.removeItem(ANALYTICS_CHOICE_KEY);
        } catch (_) {
          /* This page still fails closed if all storage access is blocked. */
        }
        apply('denied');
        return false;
      }
      storageFailure = false;
      apply(next);
      return true;
    },
    track(event, params = {}) {
      // Only the present page context is retained in memory, never an event backlog.
      if (event === 'page_view') {
        currentPage = { url: win.location.href, params };
        if (params.login_status) loginStatus = params.login_status;
      }
      if (!enabled || choice !== 'granted' || !ready || win[disableKey]) return;
      command('event', event, { ...params, send_to: measurementId });
    },
    setLoginStatus(status) {
      loginStatus = status;
      if (currentPage) {
        currentPage.params = { ...currentPage.params, login_status: status };
        delete currentPage.params.login_user_id;
      }
      if (choice === 'granted' && ready && !win[disableKey]) {
        command('set', 'user_properties', { login_status: status });
      }
    },
    dispose() {
      win.removeEventListener('storage', storageChanged);
      win.removeEventListener('focus', sync);
      doc.removeEventListener('visibilitychange', visibilityChanged);
      listeners.clear();
    },
  };
}

export const analyticsConsent = createAnalyticsConsent({
  win: window,
  doc: document,
  measurementId: process.env.REACT_APP_GA_MEASUREMENT_ID,
});
